/*
 * Pure helpers for the USCIS pane: receipt-number normalization and
 * validation, semantic status tones, and the derived summary / label the
 * pane renders. Kept free of React so it can be unit-tested in isolation.
 */
import type {
  USCISError,
  USCISReceipt,
  USCISReceiptHistory
} from "../../../api/types/case";

/** Trim, uppercase, and strip spaces / hyphens from a typed receipt number. */
export const normalizeReceiptNumber = (raw: string): string =>
  raw.trim().toUpperCase().replace(/[\s-]/g, "");

/** USCIS receipt numbers are 3 letters followed by 10 digits (13 chars). */
export const isValidReceiptNumber = (value: string): boolean =>
  /^[A-Z]{3}\d{10}$/.test(normalizeReceiptNumber(value));

/**
 * Split free text (typed or pasted) into normalized receipt-number tokens.
 * Splits on whitespace, commas and semicolons; drops empties; dedupes while
 * preserving first-seen order. Tokens are not validated here.
 */
export const parseReceiptTokens = (text: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of text.split(/[\s,;]+/)) {
    const normalized = normalizeReceiptNumber(part);
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      out.push(normalized);
    }
  }
  return out;
};

/** Newest-first copy of a receipt's status history. */
export const sortHistory = (
  history: readonly USCISReceiptHistory[] | undefined | null
): USCISReceiptHistory[] =>
  [...(history ?? [])].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

/* ---------------------------------------------------------------------- */
/* Status tones                                                            */
/* ---------------------------------------------------------------------- */

export type StatusTone =
  | "approved"
  | "attention"
  | "denied"
  | "pending"
  | "error"
  | "none";

const APPROVED_RE =
  /\b(approved|approval|card was (?:mailed|delivered|produced)|new card is being produced)\b/i;
const DENIED_RE = /\b(denied|rejected|terminated|revoked|withdrawn)\b/i;
const ATTENTION_RE =
  /(request for (?:additional )?evidence|intent to deny|intent to revoke|appointment|interview|notice .*?was (?:sent|mailed)|fee was)/i;

/** Classify USCIS status text into a semantic tone. `null` -> "none". */
export const statusTone = (text: string | null | undefined): StatusTone => {
  if (!text?.trim()) return "none";
  if (DENIED_RE.test(text)) return "denied";
  if (APPROVED_RE.test(text)) return "approved";
  if (ATTENTION_RE.test(text)) return "attention";
  return "pending";
};

/** Higher rank = more urgent; drives `aggregateTone`. */
export const toneRank: Record<StatusTone, number> = {
  denied: 5,
  error: 4,
  attention: 3,
  approved: 2,
  pending: 1,
  none: 0
};

/* ---------------------------------------------------------------------- */
/* Summary                                                                 */
/* ---------------------------------------------------------------------- */

export type ReceiptSummary = {
  receiptId: string;
  number: string;
  formType: string | null;
  latestStatus: string | null;
  latestDate: string | null;
  submittedDate: string | null;
  history: USCISReceiptHistory[];
  error: USCISError | null;
  tone: StatusTone;
};

/** Flatten a receipt into the fields the popover renders. */
export const receiptSummary = (receipt: USCISReceipt): ReceiptSummary => {
  const status = receipt.uscis_case_status;
  const history = sortHistory(status?.history);
  const latest = history[0];
  const latestStatus =
    latest?.status_en || status?.status_text_en?.trim() || null;
  const error = receipt.uscis_error ?? null;
  return {
    receiptId: receipt.receipt_id,
    number: receipt.uscis_receipt_number,
    formType: status?.form_type?.trim() || null,
    latestStatus,
    latestDate: latest?.date || status?.modified_date || null,
    submittedDate: status?.submitted_date || null,
    history,
    error,
    tone: error ? "error" : statusTone(latestStatus)
  };
};

/** Most urgent tone across all receipts. */
export const aggregateTone = (
  receipts: readonly USCISReceipt[]
): StatusTone => {
  let best: StatusTone = "none";
  for (const receipt of receipts) {
    const { tone } = receiptSummary(receipt);
    if (toneRank[tone] > toneRank[best]) best = tone;
  }
  return best;
};

/* ---------------------------------------------------------------------- */
/* Labels                                                                  */
/* ---------------------------------------------------------------------- */

const SHORT_STATUS: Array<[RegExp, string]> = [
  [
    /^response to uscis'? request for evidence was received$/i,
    "RFE response received"
  ],
  [/^request for (?:additional )?evidence was (?:sent|mailed)$/i, "RFE sent"],
  [/^fingerprint fee was received$/i, "Biometrics fee received"],
  [/^case was updated to show fingerprints were taken$/i, "Biometrics taken"],
  [/^new card is being produced$/i, "Card in production"],
  [/^card was (mailed|delivered) to me$/i, "Card $1"],
  [/^notice of intent to deny was (?:sent|mailed)$/i, "NOID sent"]
];

/** Chip-length phrasing for a USCIS status. */
export const shortStatus = (text: string): string => {
  const trimmed = text.trim().replace(/\s+/g, " ");
  for (const [re, replacement] of SHORT_STATUS) {
    if (re.test(trimmed)) return trimmed.replace(re, replacement);
  }
  const stripped = trimmed.replace(/^case was\s+/i, "");
  if (!stripped) return trimmed;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
};

const MINOR_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "by",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to"
]);

/** Title case that keeps acronyms ("RFE") and lowercases minor words. */
export const titleCase = (text: string): string =>
  text
    .split(" ")
    .map((word, index) =>
      index > 0 && MINOR_WORDS.has(word.toLowerCase())
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");

/** Compact status label for the USCIS pane header. */
export const chipLabel = (receipts: readonly USCISReceipt[]): string => {
  if (!receipts.length) return "USCIS";
  if (receipts.length === 1) {
    const { latestStatus, error } = receiptSummary(receipts[0]);
    if (error) return "Status Unavailable";
    return latestStatus
      ? titleCase(shortStatus(latestStatus))
      : "Awaiting Update";
  }
  return `${receipts.length} Receipts`;
};

/* ---------------------------------------------------------------------- */
/* Dates                                                                   */
/* ---------------------------------------------------------------------- */

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Locale date, or a dash when the value is missing / unparseable. */
export const formatReceiptDate = (iso: string | null | undefined): string => {
  const date = parseDate(iso);
  return date ? date.toLocaleDateString() : "-";
};

/** Short "Sep 12" style date for the timeline column. */
export const formatShortDate = (iso: string | null | undefined): string => {
  const date = parseDate(iso);
  return date
    ? date.toLocaleDateString(undefined, { month: "short", day: "2-digit" })
    : "-";
};

/** "today" / "yesterday" / "Nd ago" (up to 30 days) / short date. */
export const relativeUpdated = (
  iso: string | null | undefined,
  now: Date = new Date()
): string | null => {
  const date = parseDate(iso);
  if (!date) return null;
  const startOf = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(date)) / DAY_MS);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days <= 30) return `${days}d ago`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric"
  });
};

/** Newest `latestDate` across receipts, for the popover caption. */
export const latestUpdateAcross = (
  receipts: readonly USCISReceipt[]
): string | null => {
  let best: { iso: string; ms: number } | null = null;
  for (const receipt of receipts) {
    const iso = receiptSummary(receipt).latestDate;
    const date = parseDate(iso);
    if (iso && date && (!best || date.getTime() > best.ms)) {
      best = { iso, ms: date.getTime() };
    }
  }
  return best?.iso ?? null;
};

/* ---------------------------------------------------------------------- */
/* Errors                                                                  */
/* ---------------------------------------------------------------------- */

export type FriendlyUscisError = {
  title: string;
  body: string;
  /** Worth a Retry from here (USCIS was down or slow, not a bad number). */
  retryable: boolean;
};

/**
 * What a failed status fetch means for the attorney, in words. The receipt
 * stays tracked either way; only the status could not be read.
 */
export const friendlyUscisError = (
  error: USCISError | null | undefined
): FriendlyUscisError => {
  const code = (error?.code ?? "").toUpperCase();
  const status = Number(error?.status ?? (/^\d+$/.test(code) ? code : NaN));
  if (status === 404 || code.includes("NOT_FOUND"))
    return {
      title: "USCIS doesn't show this receipt yet",
      body: "New receipts can take a few days to appear in USCIS's system. Check the number, or try again later.",
      retryable: true
    };
  if (status === 429 || code.includes("RATE"))
    return {
      title: "USCIS is limiting requests",
      body: "Too many status checks at once. The receipt is saved; try again in a minute.",
      retryable: true
    };
  if (status === 400 || code.includes("INVALID"))
    return {
      title: "USCIS didn't accept this receipt number",
      body: "Check it against the receipt notice: three letters and ten digits.",
      retryable: false
    };
  return {
    title: "USCIS isn't responding right now",
    body: "Their case status service is down or slow. The receipt is saved and will update on its own; you can also try again.",
    retryable: true
  };
};

/* ---------------------------------------------------------------------- */
/* Suggestions                                                             */
/* ---------------------------------------------------------------------- */

export type SuggestedReceipt = { number: string; from: string };

const MAX_SCAN_DEPTH = 3;

const receiptsIn = (
  value: unknown,
  depth: number,
  found: (number: string, from: string | null) => void,
  from: string | null = null
): void => {
  if (depth > MAX_SCAN_DEPTH || !value || typeof value !== "object") return;
  if (Array.isArray(value)) {
    value.forEach((v) => receiptsIn(v, depth + 1, found, from));
    return;
  }
  const row = value as Record<string, unknown>;
  const here = typeof row.exhibit === "string" ? row.exhibit : from;
  Object.entries(row).forEach(([key, v]) => {
    if (key === "receipt_number" && typeof v === "string") found(v, here);
    else receiptsIn(v, depth + 1, found, here);
  });
};

/**
 * Receipt numbers the case's documents carry (read into its key facts) that
 * are not tracked yet, with the document each came from.
 */
export const suggestedReceipts = (
  facts: ReadonlyArray<{ label: string; status: string; value: unknown }>,
  tracked: ReadonlySet<string>
): SuggestedReceipt[] => {
  const out = new Map<string, SuggestedReceipt>();
  facts
    .filter((f) => f.status !== "disabled")
    .forEach((fact) =>
      receiptsIn(fact.value, 0, (raw, from) => {
        const number = normalizeReceiptNumber(raw);
        if (!isValidReceiptNumber(number) || tracked.has(number)) return;
        if (!out.has(number))
          out.set(number, { number, from: from || fact.label });
      })
    );
  return [...out.values()];
};

/**
 * The toast after tracking receipts, saying what actually happened: added
 * with a status, added but USCIS could not be read, or added and waiting.
 */
export const addedToast = (
  added: ReadonlyArray<USCISReceipt | null>
): { title: string; description: string; tone: "success" | "warning" } => {
  const receipts = added.filter((r): r is USCISReceipt => !!r);
  const count = receipts.length || added.length;
  const title = count > 1 ? `${count} receipts tracked` : "Receipt tracked";
  const failed = receipts.filter((r) => r.uscis_error);
  if (failed.length) {
    const reason = friendlyUscisError(failed[0].uscis_error).title;
    const which =
      failed.length === count
        ? count > 1
          ? "Their status"
          : "Its status"
        : `The status of ${failed.length} of them`;
    return {
      title,
      description: `${reason}. ${which} will show once USCIS answers.`,
      tone: "warning"
    };
  }
  const latest = receipts
    .map((r) => receiptSummary(r).latestStatus)
    .find(Boolean);
  return {
    title,
    description: latest
      ? `Latest from USCIS: ${latest}`
      : "USCIS hasn't posted a status yet; updates will show here.",
    tone: "success"
  };
};
